/// <reference types="user-agent-data-types" />

import { getTestStableAnyBrowserVersion } from '@lingoda/local-feature-flags';
import {
    isChrome,
    isDesktopDevice,
    isEdge,
    isFirefox,
    isSafari,
    platformInfo,
} from '@lingoda/user-agent';

export type WellSupportedBrowser = 'chrome' | 'edge' | 'safari' | 'firefox';

export const NEW_CHROME_LINK = 'https://www.google.com/chrome';
export const NEW_EDGE_LINK = 'https://www.microsoft.com/edge';
export const NEW_SAFARI_LINK = 'https://www.apple.com/safari';
export const NEW_FIREFOX_LINK = 'https://www.mozilla.org/firefox';

// Zoom supports the current stable Chromium version and the two previous versions
// https://developers.zoom.us/docs/video-sdk/web/browser-support/
const SUPPORT_BACKWARDS_COMPATIBILITY = 2;

const MIN_SUPPORTED_BROWSER_VERSIONS: Record<WellSupportedBrowser, number> = {
    chrome: (getTestStableAnyBrowserVersion() ?? 148) - SUPPORT_BACKWARDS_COMPATIBILITY, // Check at https://chromestatus.com/roadmap
    edge: (getTestStableAnyBrowserVersion() ?? 147) - SUPPORT_BACKWARDS_COMPATIBILITY, // https://learn.microsoft.com/en-us/deployedge/microsoft-edge-relnote-stable-channel
    safari: getTestStableAnyBrowserVersion() ?? 17, // 26, 18, 17 https://developer.apple.com/documentation/safari-release-notes
    firefox: (getTestStableAnyBrowserVersion() ?? 150) - SUPPORT_BACKWARDS_COMPATIBILITY, // https://www.mozilla.org/en-US/firefox/releases/
};

export const getIsDeviceSupportingAllClassroomFeatures = () => {
    return isDesktopDevice;
};

/**
 * Returns the version of the Chromium-based browser as a number if the browser is Chromium-based
 * (i.e., if navigator.userAgentData exists and 'Chromium' is an identified brand),
 * otherwise returns null.
 * @returns {number | null} - Version number if Chromium-based browser, otherwise null.
 *
 * Example:
 * - Win11 + Chrome v139.0.7258.155 - returns 139
 * - Win11 + Firefox v147.0.0 - returns null
 * - MacOS + Safari v17.0.0 - returns null as Safari is WebKit based
 */
const getBrowserVersionIfChromiumBasedBrowser = (): number | null => {
    if (!navigator.userAgentData) {
        return null;
    }

    const chromiumBrand = navigator.userAgentData.brands.find(
        (brand) => brand.brand === 'Chromium',
    );

    return chromiumBrand ? parseInt(chromiumBrand.version) : null;
};

/**
 * Checks if current browser allows opening Document Picture-in-Picture without recent user activation (e.g. click).
 * When browser doesn't allow it, there's a "Failed to execute 'requestWindow' on 'DocumentPictureInPicture':
 * Document PiP requires user activation" error in console if we open PiP AFTER opening screen-sharing browser dialog.
 * Browser considers delay between user click and PiP opening too long.
 * By manual testing it's confirmed that:
 * - Win11 + Chrome v137.0.7151.41 - doesn't allow opening PiP without user activation
 * - Win11 + Chrome v138.0.7204.50 - doesn't allow opening PiP without user activation
 * - Win11 + Chrome v139.0.7258.155 - allows opening
 * - MacOS + Chrome v139.0.7258.155 - allows opening
 *
 * From the other hand, we cannot open PiP BEFORE opening screen-sharing dialog, because in this case the dialog
 * is opened inside PiP window, not main window.
 *
 * As a workaround for mentioned errors, we use the "enabled" workflow:
 *  first enablePipWindow()
 *  then call startShareScreen()
 *  then effect in PipContextProvider detects new enabled state and opens PiP window.
 * This way the screen-sharing dialog is opened in main window, and delay after user activation is small enough to allow opening PiP.
 * BUT this workflow is not deterministic and fragile.
 * TODO: Remove "enabled" PiP workflow as soon as user browser adoption allows it.
 *
 * @returns boolean
 */
export const isBrowserSupportsPipWithoutUserActivation = () => {
    const currentChromiumVersion = getBrowserVersionIfChromiumBasedBrowser();

    if (currentChromiumVersion === null) {
        // Not a chromium browser
        return false;
    }

    return currentChromiumVersion >= 139;
};

const getDoesBrowserVendorSupportClassroom = () => {
    return isChrome || isEdge;
};

const getDoesChromiumVersionSupportClassroom = () => {
    const currentChromiumVersion = getBrowserVersionIfChromiumBasedBrowser();

    if (currentChromiumVersion === null) {
        // Not a chromium browser
        return false;
    }

    return currentChromiumVersion >= MIN_SUPPORTED_BROWSER_VERSIONS.chrome;
};

export const getIsBrowserSupportingAllClassroomFeatures = () => {
    return getDoesBrowserVendorSupportClassroom() && getDoesChromiumVersionSupportClassroom();
};

export const isBrowserOutdated = (name: WellSupportedBrowser | null) => {
    if (!name) {
        return null;
    }

    const browserMajorVersion = platformInfo.browserMajorVersion;

    if (!browserMajorVersion) {
        return null;
    }

    return browserMajorVersion < MIN_SUPPORTED_BROWSER_VERSIONS[name];
};

export const getWellSupportedBrowserName = (): WellSupportedBrowser | null => {
    if (isChrome) {
        return 'chrome';
    }
    if (isEdge) {
        return 'edge';
    }
    if (isSafari) {
        return 'safari';
    }
    if (isFirefox) {
        return 'firefox';
    }

    return null;
};
